import { useI18n } from '@fshr/context/i18n-context';
import { Avatar, Icon, Link, LinkIfFullyAuthenticated } from '@fshr/components';
import { formatBeverlyWilshire } from '@fshr/shared';
import { PROFILE_SECTIONS } from './profile-section';

interface Props {
	name: string;
	initials: string;
	avatar?: string | null;
	upcomingTripCheckIn?: { href: string; cta: string } | null;
	isFullyAuthenticated: boolean;
}

export default function ProfileSummary({
	name,
	initials,
	avatar,
	upcomingTripCheckIn,
	isFullyAuthenticated,
}: Props) {
	const { t, localeLink } = useI18n();

	const actions = PROFILE_SECTIONS.map(section => ({
		label: t(section.label),
		description: t(section.description),
		icon: section.icon,
		href: localeLink(section.href),
		cta: t('edit'),
		ariaLabel: `${t(section.label)} - ${t('edit')}`,
		requiresFullAuthenticaton: false,
	}));

	return (
		<div
			id="profile"
			className="rounded-t-[20px] shadow-[0px_5px_15px_0px_rgba(3,3,3,0.12)] overflow-hidden"
		>
			{upcomingTripCheckIn && (
				<Link
					href={upcomingTripCheckIn.href}
					intent="unstyled"
					className="md:col-span-2 text-text-inverse-heading rounded-t-[20px] min-h-14 flex items-center justify-center text-center p-150 bg-[rgba(45,45,45,0.85)] backdrop-blur-xs text-h4 gap-150"
					prefetch={false}
					icon={<Icon icon="arrow_right" className="text-[13px]" />}
				>
					<span
						dangerouslySetInnerHTML={{ __html: formatBeverlyWilshire(upcomingTripCheckIn.cta) }}
					></span>
				</Link>
			)}

			<div className="bg-surface-raised flex flex-row items-center gap-150 px-200 md:px-300 py-200">
				<Avatar avatar={avatar} initials={initials} className="md:pt-100 pt-[7px] pl-[3px]" />
				<p className="text-h3 text-text-heading">{name}</p>
			</div>

			<div className="bg-surface-raised-subtle px-200 md:px-300">
				<div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-300">
					{actions.map(action => (
						<div
							key={action.label}
							className="py-200 flex items-center gap-200 border-b border-border-subtle last:border-b-0 md:[&:nth-last-child(-n+2)]:border-b-0"
						>
							<div className="flex flex-col gap-060 grow min-w-0">
								<div className="flex items-center gap-100">
									<span aria-hidden="true">
										<Icon icon={action.icon} className="text-[20px]" />
									</span>
									<span className="text-subtitle2 text-text-heading">{action.label}</span>
								</div>
								<p className="text-caption text-text-primary">{action.description}</p>
							</div>
							{action.requiresFullAuthenticaton ? (
								<LinkIfFullyAuthenticated
									isFullyAuthenticated={isFullyAuthenticated}
									href={action.href}
									prefetch={false}
									aria-label={action.ariaLabel}
								>
									{action.cta}
								</LinkIfFullyAuthenticated>
							) : (
								<Link href={action.href} prefetch={false} aria-label={action.ariaLabel}>
									{action.cta}
								</Link>
							)}
						</div>
					))}
				</div>
			</div>
		</div>
	);
}
